"use client";

import React from "react";
import { Snowflake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const packagesData = [{
  title: "The Essential",
  price: "$2,800",
  description: "Perfect for intimate ceremonies and elopements.",
  features: ["6 Hours of continuous coverage", "High-resolution digital gallery", "Personalized timeline planning", "Soft-toned artistic editing"],
  isPopular: false,
  buttonText: "Inquire for Availability",
  link: "/InquiryPage"
}, {
  title: "The Signature",
  price: "$4,200",
  description: "Our most requested collection for full-day stories.",
  features: ["9 Hours of continuous coverage", "Engagement session included", "Second professional photographer", "Heirloom linen-bound album", "Online gallery for 12 months"],
  isPopular: true,
  buttonText: "Inquire for Availability",
  link: "/InquiryPage"
}, {
  title: "The Heirloom",
  price: "$6,500",
  description: "The complete Ivory Lens experience for your legacy.",
  features: ["Full day unlimited coverage", "Engagement & Rehearsal sessions", "Large format fine-art album", "Two parent companion albums", "Priority 4-week gallery delivery"],
  isPopular: false,
  buttonText: "Inquire for Availability",
  link: "/InquiryPage"
}];
export default function Packages() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="239930" id="investment-collections" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-[600px] bg-primary/5 blur-[120px] rounded-lg pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="text-4xl md:text-5xl font-default font-light mb-6 tracking-tight">
            Investment & Collections
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Timeless imagery tailored to your unique story. Each collection is 
            curated to provide a seamless experience and lasting memories.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 items-center max-w-6xl mx-auto transition-all duration-1000 opacity-0 translate-y-8", isVisible && "opacity-100 translate-y-0")}>
          {packagesData.map((pkg, i) => <div data-index={i} key={i} className={cn("relative group h-full transition-all duration-500", pkg.isPopular ? "md:scale-110 z-10" : "z-0")}>
              {}
              {pkg.isPopular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <Badge variant="outline" className="bg-background/80 backdrop-blur-md border-primary/20 text-primary px-4 py-1 text-xs tracking-widest font-sans font-medium uppercase">
                    Most Coveted
                  </Badge>
                </div>}

              <Card className={cn("h-full flex flex-col relative overflow-hidden transition-all duration-500", "bg-card/30 backdrop-blur-sm border-border/50", "group-hover:bg-card/50 group-hover:border-primary/20 group-hover:shadow-xl group-hover:shadow-primary/5", pkg.isPopular ? "border-primary/20 shadow-lg shadow-primary/5" : "")}>
                {}
                <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-white/5 pointer-events-none" />

                <CardHeader className="text-center pb-4 pt-8">
                  <CardTitle className="text-3xl font-default font-light mb-2">
                    {pkg.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground font-sans h-10">
                    {pkg.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow flex flex-col items-center pt-4">
                  <div className="mb-8 text-center">
                    <span className="text-4xl lg:text-5xl font-default font-medium text-foreground tracking-tighter">
                      {pkg.price}
                    </span>
                  </div>

                  <ul className="w-full space-y-4">
                    {pkg.features.map((feature, fIndex) => <li data-index={fIndex} key={fIndex} className="flex items-start gap-3 text-sm text-muted-foreground group-hover:text-foreground/90 transition-colors duration-300">
                        <Snowflake className="w-4 h-4 text-primary/40 shrink-0 mt-0.5 opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
                        <span className="leading-tight font-sans">{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 pb-8">
                  <Button asChild variant={pkg.isPopular ? "default" : "outline"} className={cn("w-full h-12 text-sm font-sans tracking-widest uppercase transition-all duration-300 rounded-md", !pkg.isPopular && "bg-transparent border-border text-foreground hover:bg-primary hover:text-primary-foreground")}>
                    <Link to={pkg.link}>{pkg.buttonText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}